import React from "react";
import Select, { components } from "react-select";
import { Controller, useFormContext, UseControllerProps } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ScheduleResourceResponse } from "app/utils/request/scheduling/facilityResources";
import MultiValue from "app/components/SchedulingUserSelector/MultiValue";
import userSelectorStyles from "app/components/SchedulingUserSelector/styles.module.scss";
import { cls } from "app/utils";
import { SelectStyles } from "./constants";
import { ResourceOption, groupBySection } from "./ResourceSelect";

type PropsDef = {
  name: string;
  // The active bays, in the order of the Resources page.
  bays: ScheduleResourceResponse[];
  // The bays of the block, for a bay that the list does not offer (an
  // inactive bay). The value shows its title.
  heldResources?: { id: number; title: string }[];
  rules?: UseControllerProps["rules"];
  disabled?: boolean;
};

type BayOption = { id: number; title: string };

// Adds the bay, or removes it when the list has it already. The coach select
// of the block form does the same with a coach.
function toggle(ids: number[], id: number): number[] {
  return ids.includes(id)
    ? ids.filter((current) => current !== id)
    : [...ids, id];
}

// One bay in the menu, with the check mark of the coach select.
function BayMenuOption(props: any) {
  return (
    <div
      className={cls(
        userSelectorStyles.option,
        props.isSelected && userSelectorStyles.selected,
      )}
      onClick={() => props.selectOption(props.data)}
      data-testid="block-resource-option"
    >
      <div className={userSelectorStyles.option_info}>
        <div className={cls("option__label", userSelectorStyles.option_label)}>
          {props.label}
        </div>
      </div>
      <div
        className={cls(
          userSelectorStyles.selection_status,
          props.isSelected && userSelectorStyles.selected,
        )}
      >
        {props.isSelected ? (
          <i className="ico ico-check" />
        ) : (
          <div className={userSelectorStyles.blank_selection} />
        )}
      </div>
    </div>
  );
}

/**
 * The bays of a block in the block form. The select works as the coach
 * select of the same form: the menu starts with an "All" entry, each bay has
 * a check mark, the control shows the count, and the chosen bays show under
 * the control with a remove button. The bays show in groups by section, in
 * the order of the Resources page, as in the resource select of a lesson.
 */
export default function ResourceMultiSelect({
  name,
  bays,
  heldResources = [],
  rules,
  disabled = false,
}: PropsDef) {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const groups = groupBySection(bays);
  const allOption = { label: t("All Resources"), value: "all" };
  const bayOptions = bays.map((bay) => ({ id: bay.id, title: bay.title }));

  const optionFor = (id: number): BayOption | null => {
    const listed = bayOptions.find((option) => option.id === id);
    if (listed) {
      return listed;
    }
    const held = heldResources.find((resource) => resource.id === id);
    return held ? { id: held.id, title: held.title } : null;
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const ids: number[] = field.value ?? [];
        const selection = ids
          .map(optionFor)
          .filter((option): option is BayOption => option !== null);
        const allSelected =
          bayOptions.length > 0 &&
          bayOptions.every((option) => ids.includes(option.id));

        // The control shows "All Resources" when every bay is on, else the
        // count, as the coach select does.
        const ValueWithAllOption = (valueProps: any) =>
          allSelected ? (
            <div className={userSelectorStyles.multi_value}>
              {allOption.label}
            </div>
          ) : (
            <MultiValue {...valueProps} />
          );

        const MenuListWithAllOption = (menuProps: any) => (
          <components.MenuList {...menuProps}>
            <div
              className={userSelectorStyles.option}
              onClick={() => {
                field.onChange(bayOptions.map((option) => option.id));
                menuProps.selectProps.onMenuClose();
              }}
              data-testid="block-resource-all"
            >
              <div className={userSelectorStyles.option_info}>
                <div className="option__label">{allOption.label}</div>
              </div>
            </div>
            {menuProps.children}
          </components.MenuList>
        );

        return (
          <div>
            <Select
              styles={SelectStyles}
              aria-label={t("Resources")}
              placeholder={t("Select Resources")}
              getOptionLabel={(option: ResourceOption) => option.title}
              getOptionValue={(option: ResourceOption) => String(option.id)}
              options={groups}
              value={selection as any}
              components={{
                Option: BayMenuOption,
                SingleValue: ValueWithAllOption,
                MenuList: MenuListWithAllOption,
              }}
              onChange={(option: any) => {
                if (option?.id != null) {
                  field.onChange(toggle(ids, option.id));
                }
              }}
              onBlur={field.onBlur}
              isSearchable
              isDisabled={disabled}
            />

            {selection.length > 0 && (
              <div
                className={userSelectorStyles.list_container}
                data-testid="block-resource-chips"
              >
                {selection.map((bay) => (
                  <div key={bay.id} className={userSelectorStyles.list_item_pill}>
                    <span>{bay.title}</span>
                    {!disabled && (
                      <div
                        className={userSelectorStyles.remove_item_pill}
                        onClick={() => field.onChange(toggle(ids, bay.id))}
                        role="button"
                        aria-label={t("Remove")}
                      >
                        <i className="ico ico-x" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}
